import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Users, LogIn, LogOut, Ban, CalendarRange, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export const GroupActions = ({ booking, onDone, size = "sm" }) => {
  const [shiftOpen, setShiftOpen] = useState(false);
  const [days, setDays] = useState(1);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const canDelete = user?.role === "admin" || !!user?.can_delete_bookings;
  if (!booking?.group_id || !(booking.group_size > 1)) return null;
  const gid = booking.group_id;
  const run = async (fn, okMsg) => {
    setBusy(true);
    try { const { data } = await fn(); toast.success(okMsg(data)); onDone && onDone(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const setStatus = (status, label) => run(() => api.patch(`/bookings/group/${gid}/status`, { status }), (d) => `${label} applicato a ${d.updated} camere`);
  const shift = () => { setShiftOpen(false); run(() => api.patch(`/bookings/group/${gid}/shift`, { days: Number(days) }), (d) => `Gruppo spostato di ${days} giorni (${d.updated} camere)`); };
  const remove = () => {
    if (!window.confirm(`Eliminare tutte le ${booking.group_size} prenotazioni del gruppo di ${booking.guest_name}?`)) return;
    run(() => api.delete(`/bookings/group/${gid}`), (d) => `Gruppo eliminato (${d.deleted} prenotazioni)`);
  };
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size={size} data-testid={`group-actions-${booking.id}`} disabled={busy} className="border-sky-300 text-sky-800 hover:bg-sky-50">
            <Users className="w-4 h-4 mr-1.5" />Gruppo ({booking.group_size})
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Tutte le {booking.group_size} camere</DropdownMenuLabel>
          <DropdownMenuItem data-testid="group-checkin" onClick={() => setStatus("checkedin", "Check-in")}><LogIn className="w-4 h-4 mr-2" />Check-in</DropdownMenuItem>
          <DropdownMenuItem data-testid="group-checkout" onClick={() => setStatus("checkedout", "Check-out")}><LogOut className="w-4 h-4 mr-2" />Check-out</DropdownMenuItem>
          <DropdownMenuItem data-testid="group-cancel" onClick={() => setStatus("cancelled", "Annullamento")}><Ban className="w-4 h-4 mr-2" />Annulla prenotazioni</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="group-shift" onClick={() => setShiftOpen(true)}><CalendarRange className="w-4 h-4 mr-2" />Sposta date…</DropdownMenuItem>
          {canDelete && <>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="group-delete" onClick={remove} className="text-destructive focus:text-destructive"><Trash2 className="w-4 h-4 mr-2" />Elimina gruppo</DropdownMenuItem>
          </>}
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={shiftOpen} onOpenChange={setShiftOpen}>
        <DialogContent className="max-w-sm" data-testid="group-shift-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Sposta il gruppo</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Tutte le {booking.group_size} camere verranno spostate dello stesso numero di giorni (negativo = anticipa). Se una camera risulta occupata, nessuna viene spostata.</p>
          <div><Label className="text-xs uppercase tracking-wider">Giorni</Label><Input type="number" data-testid="group-shift-days" className="mt-1.5" value={days} onChange={(e) => setDays(e.target.value)} min={-365} max={365} /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShiftOpen(false)}>Annulla</Button>
            <Button data-testid="group-shift-confirm" onClick={shift} disabled={busy || Number(days) === 0} className="bg-accent hover:bg-accent/90 text-accent-foreground">Sposta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
